<template>
  <div ref="benefitsContainer">
    <ContainerLayout class="tw:py-11 tw:flex tw:flex-col">
      <InsightsHeader title="plus_premium_benefits_header" subtitle="plus_premium_benefits_subtext" />
      <div class="tw:flex tw:flex-wrap tw:gap-5 tw:w-full">
        <div
          v-for="benefit in benefitsList"
          :key="benefit.id"
          :class="[
            'tw:w-full tw:flex  tw:md:w-[calc(33.333%-1.334rem)]',
            { 'tw:hidden tw:md:flex': !benefit.isMobile },
          ]"
        >
          <BenefitCard
            :id="benefit.id"
            :title="benefit.title"
            :text="benefit.text"
            :buttonLabel="benefit.buttonLabel"
            :icon="benefit.icon"
            :isActive="benefit.isActive"
            :route="benefit.route"
          />
        </div>
      </div>
      <div class="tw:mt-8 tw:flex tw:justify-center tw:md:hidden">
        <button v-if="!showAll" @click="showAll = true" class="tw:text-blue-700 tw:font-bold">
          {{ $t('show_more') }}
        </button>
      </div>
    </ContainerLayout>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'

import ContainerLayout from '@/layouts/ContainerLayout.vue'
import InsightsHeader from '@/components/studioPlus/insights/InsightsHeader.vue'
import BenefitCard from '@/components/studioPlus/insights/BenefitCard.vue'

import { selectedAccommodationId } from '@/layouts/queries'
import { useActiveOffer } from '@/components/hotelOffers/queries'
import { useCompetitors } from '@/components/rateInsights/queries'

import appAnalytics from '@/utils/tracking'

const hotelOffersIcon = new URL('/src/assets/img/studio-plus-insights/hotel-offers-icon.svg', import.meta.url)
const rateInsightsIcon = new URL('/src/assets/img/studio-plus-insights/rate-insights-icon.svg', import.meta.url)
const contactBadgeIcon = new URL('/src/assets/img/studio-plus-insights/contact-badge-icon.svg', import.meta.url)
const supportIcon = new URL('/src/assets/img/studio-plus-insights/support-icon.svg', import.meta.url)

const { hasActiveOffer } = useActiveOffer(selectedAccommodationId)
const { hasCompetitors } = useCompetitors(selectedAccommodationId)

const showAll = ref(true)
const benefitsContainer = ref(null)
const isInViewport = ref(false)
let observer = null

onMounted(() => {
  observer = new IntersectionObserver(
    ([entry]) => {
      isInViewport.value = entry.isIntersecting

      if (entry.isIntersecting) {
        if (selectedAccommodationId.value) {
          appAnalytics.track(appAnalytics.events.SP_PLUS_INSIGHTS_BENEFITS_VIEWED, {
            item_id: selectedAccommodationId.value,
          })
        }
        if (observer) {
          observer.disconnect()
          observer = null
        }
      }
    },
    { threshold: 0.1 }
  )

  if (benefitsContainer.value) {
    observer.observe(benefitsContainer.value)
  }
})

onUnmounted(() => {
  if (observer) {
    observer.disconnect()
  }
})

const isFeatureUsed = featureId => {
  if (featureId === 'hotel-offer-badge') {
    return hasActiveOffer.value
  }
  if (featureId === 'rate-insights' || featureId === 'visitors-profile') {
    return hasCompetitors.value
  }
}

const benefitsList = computed(() => [
  {
    id: 'hotel-offer-badge',
    title: 'hotel_offers_product_header',
    text: 'plus_insights_ho_subtext',
    buttonLabel: isFeatureUsed('hotel-offer-badge') ? 'plus_ho_cta' : 'plus_insights_ho_cta',
    isActive: isFeatureUsed('hotel-offer-badge'),
    icon: hotelOffersIcon,
    route: isFeatureUsed('hotel-offer-badge')
      ? { name: 'hotel-offers' }
      : { name: 'hotel-offers', query: { from_insights: 'true' } },
    isMobile: false,
  },
  {
    id: 'rate-insights',
    title: 'app.rateInsights.name',
    text: 'plus_insights_ri_subtext',
    buttonLabel: isFeatureUsed('rate-insights') ? 'plus_ri_cta' : 'rate_insights_inactive_cta',
    isActive: isFeatureUsed('rate-insights'),
    icon: rateInsightsIcon,
    route: isFeatureUsed('rate-insights')
      ? { name: 'rate-insights' }
      : { name: 'rate-insights-onboarding', query: { from_insights: 'true' } },
    isMobile: false,
  },
  {
    id: 'visitors-profile',
    title: 'app.visitorsProfile.name',
    text: 'plus_insights_vp_subtext',
    buttonLabel: isFeatureUsed('visitors-profile') ? 'plus_insights_vp_cta' : 'visitors_profile_inactive_cta',
    isActive: isFeatureUsed('visitors-profile'),
    icon: rateInsightsIcon,
    route: isFeatureUsed('visitors-profile')
      ? { name: 'visitors-profile' }
      : { name: 'rate-insights-onboarding', query: { from_insights: 'true', from_visitors_profile: 'true' } },
    isMobile: false,
  },
  {
    id: 'contact-badge',
    title: 'plus_contact_badge',
    text: 'plus_insights_contact_badge_subtext',
    buttonLabel: 'plus_insights_contact_details_cta',
    isActive: true,
    icon: contactBadgeIcon,
    route: { name: 'property-details-basic-information', query: { open_update: true, from_insights: 'true' } },
    isMobile: true,
  },
  {
    id: 'dedicated_support',
    title: 'plus_insights_support_header',
    text: 'plus_insights_support_subtext',
    buttonLabel: 'plus_insights_support_cta',
    isActive: true,
    icon: supportIcon,
    route: '',
    isMobile: true,
  },
])
</script>
